import React, { useEffect, useState, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { customStringify, customTableStyles, formatFollowers } from '../../../utils';
import { CATEGORIES, CONTENT_TYPE, DATE_FORMAT_DISPLAY, DATETIME_FORMAT_DISPLAY, DESENDING_ORDER, LISTING_LIMIT } from '../../../constants';
import Loading from '../../../components/Loading';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import FormattedCurrency from '../../../components/FormattedCurrency';
import { Tooltip } from 'react-tooltip';
import { StatusTag } from '../../../components/Reuseable/Tag';
import useApi from '../../../hooks/useApi';
import CampaignsTable from './CampaignsTable';
import { DateTime } from 'luxon';
import { GrInstagram } from 'react-icons/gr';
import { FaTiktok } from 'react-icons/fa6';

const Table = ({
  loading,
  setSort,
  currentPage,
  setCurrentPage,
  count,
  data
}) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [expandedRows, setExpandedRows] = useState([]);
  const [campaignsData, setCampaignsData] = useState({});
  const [loadingCampaigns, setLoadingCampaigns] = useState({});
  const { Axios } = useApi();

  const [campaignsPage, setCampaignsPage] = useState({}); // Track page for each campaign

  const fetchCampaigns = useCallback(async (influencerId, page = 1) => {
    try {
      setLoadingCampaigns(prev => ({ ...prev, [influencerId]: true }));
      const offset = (page - 1) * LISTING_LIMIT;
      const result = await Axios.get(`api/influencer/influencer-campaign/${influencerId}?limit=${LISTING_LIMIT}&offset=${offset}`);
      setCampaignsData(prev => ({
        ...prev,
        [influencerId]: {
          data: result.data.payload,
          count: result.data.count
        }
      }));
      setCampaignsPage(prev => ({ ...prev, [influencerId]: page }));
    } catch (error) {
      console.error('Error fetching deliverables:', error);
    } finally {
      setLoadingCampaigns(prev => ({ ...prev, [influencerId]: false }));
    }
  }, [Axios]);

  const handleRowToggle = useCallback(async (row) => {
    const rowId = row.id;

    if (expandedRows.includes(rowId)) {
      setExpandedRows(expandedRows.filter(id => id !== rowId));
    } else {
      setExpandedRows([...expandedRows, rowId]);

      if (!campaignsData[rowId]) {
        await fetchCampaigns(rowId);
      }
    }
  }, [expandedRows, campaignsData, fetchCampaigns]);

  const columns = [
    {
      name: '',
      cell: row => {
        const isExpanded = expandedRows.includes(row.id);
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRowToggle(row);
            }}
            className="p-2 text-primary w-full flex justify-center items-center"
            data-tooltip-id="expand-tooltip"
            data-tooltip-content={intl.formatMessage({
              id: isExpanded ? "collapse_details" : "expand_details",
              defaultMessage: isExpanded ? "Collapse Details" : "Expand Details"
            })}
          >
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
          </button>
        );
      },
      width: '60px',
      ignoreRowClick: true,
    },
   {
         name: <div className='min-w-[400px]'><FormattedMessage id="influencer" defaultMessage={"Influencer"} /></div>,
         selector: row => row.user?.name,
         // sortable: true,
         wrap: true,
   
   
         cell: row => (
           <div className={` flex items-center gap-2 py-2 ${row.isBlurred ? "blur-sm select-none" : ""}`}>
             <div
               className="min-w-14 w-14 min-h-14 h-14 rounded-full bg-gray-200 bg-cover bg-center"
               style={{ backgroundImage: `url(${row.user?.picture || "/images/avatar.png"})` }}
             ></div>
   
   
             <div>
               {
                 row.isBlurred ?
                   <p className='text-gray-900  font-medium text-sm'>{row.user?.name} {row.user?.sur_name}</p>
                   :
                   <Link
                     to={`/influencer/${row.id}`}
                     className="text-gray-900 hover:text-primary font-medium text-sm"
                   >
                     {row.user?.name} {row.user?.sur_name}
                   </Link>
               }
             </div>
           </div>
         )
       },
    // {
    //   name: <FormattedMessage id="cost" defaultMessage="Cost" />,
    //   cell: row => row.budget ? <FormattedCurrency value={row.budget} /> : '-',
    // },
    {
          name: <><FormattedMessage id="followers" defaultMessage={"Followers"} /></>,
          selector: row => row.instagram_followers || row.tiktok_followers,
          wrap: true,
    
          cell: row => (
            // <span className={`text-sm font-medium ${row.isBlurred ? "blur-sm select-none " : ""}`}>
            //   {formatFollowers(row.instagram_followers || row.tiktok_followers)}
            // </span>
    
            <div className={`text-sm pl-2 font-medium ${row.isBlurred ? "blur-sm select-none " : ""}`}>
              {
                row.instagram_followers > 0 &&
                <div className='inline-flex space-x-1 items-center'>
                  <GrInstagram className='text-sm text-gray-500' />
                  <span>{formatFollowers(row.instagram_followers)}</span>
                </div>
              }
              {
                row.tiktok_followers > 0 &&
                <div>
                  <div className='inline-flex space-x-1 items-center'>
                    <FaTiktok className='text-sm text-gray-500' />
                    <span>{formatFollowers(row.tiktok_followers)}</span>
                  </div>
                </div>
              }
              {
                row?.instagram_followers === 0 && row?.tiktok_followers === 0 &&
                <div className='inline-flex space-x-1 items-center'>
                  <GrInstagram className='text-sm text-gray-500' />
                  <span>0</span>
                </div>
              }
            </div>
          )
        },
   {
         name: <><FormattedMessage id="engagement" defaultMessage={"Engagement"} /></>,
         selector: row => row.engagement_rate,
         wrap: true,
   
         cell: row => (
           // <span className={`text-sm font-medium ${row.isBlurred ? "blur-sm" : ""}`}>
           //   {row.engagement_rate || '0%'}
           // </span>
           <div className={`text-sm pl-2 font-medium ${row.isBlurred ? "blur-sm select-none " : ""}`}>
             {
               row.instagram_followers > 0 &&
               <div className='inline-flex space-x-1 items-center'>
                 <GrInstagram className='text-sm text-gray-500' />
                 <span>{formatFollowers(row.instagram_engagement_metrics)}%</span>
               </div>
             }
             {
               row.tiktok_followers > 0 &&
               <div>
                 <div className='inline-flex space-x-1 items-center'>
                   <FaTiktok className='text-sm text-gray-500' />
                   <span>{formatFollowers(row.tiktok_engagement_metrics)}%</span>
                 </div>
               </div>
             }
             {
               row?.instagram_followers === 0 && row?.tiktok_followers === 0 &&
               <div className='inline-flex space-x-1 items-center'>
                 <GrInstagram className='text-sm text-gray-500' />
                 <span>0%</span>
               </div>
             }
             </div>
         )
       },
    {
          name: <><FormattedMessage id="niche" defaultMessage={"Niche"} /></>,
          selector: row => row.engagement_rate,
          wrap: true,
    
          cell: row => (
            // <span className={`text-sm font-medium ${row.isBlurred ? "blur-sm" : ""}`}>
            //   {row.engagement_rate || '0%'}
            // </span>
            <div className={`text-sm font-medium ${row.isBlurred ? "blur-sm select-none " : ""}`}>
              {row.niches && row.niches.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {row.niches.slice(0, 2).map((niche, index) => (
                    <span
                      key={index}
                      className="rounded-full text-xs text-white bg-secondary py-1 px-3"
                    >
                      {CATEGORIES.find((row) => row.value === niche)?.label ? CATEGORIES.find((row) => row.value === niche)?.label : niche}
                    </span>
                  ))}
                  {row.niches.length > 2 && (
                    <span className="rounded-full text-xs text-white bg-secondary py-1 px-3">
                      +{row.niches.length - 2}
                    </span>
                  )}
                </div>
              )}</div>
          )
        },
    {
          name: <><FormattedMessage id="location" defaultMessage={"Location"} /></>,
          selector: row => row.user?.city,
          wrap: true,
    
    
          cell: row => (
            <span className={`text-sm text-gray-600 ${row.isBlurred ? "blur-sm select-none " : ""}`}>
              {`${row.user?.country || ''}${row.user?.country && row.user?.city ? ', ' : ''}${row.user?.city || ''}${row.user?.state ? `, ${row.user?.state}` : ''}`}
            </span>
          )
        }
  ];

  const handleSort = (column, direction) => {
    setSort({
      title: column.selector || column.name,
      order: direction,
    });
  };

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  return (
    <div className="pb-10">
      <div className="relative rounded-[6px] shadow-right">
        <DataTable
          columns={columns}
          data={data}
          customStyles={customTableStyles}
          progressComponent={<Loading />}
          progressPending={loading}
          noDataComponent={
            <span className="py-8">
              <FormattedMessage
                id="no_influencers_found"
                defaultMessage="No Influencers Found"
              />
            </span>
          }
          sortServer
          onSort={handleSort}
          pagination
          paginationServer
          paginationTotalRows={count}
          paginationDefaultPage={currentPage}
          paginationPerPage={LISTING_LIMIT}
          onChangePage={handlePageChange}
          paginationComponentOptions={{ noRowsPerPage: true }}
          expandableRows
          expandableRowsComponent={({ data: row }) => (
            <CampaignsTable
              influencerId={row.id}
              data={campaignsData[row.id]?.data || []}
              count={campaignsData[row.id]?.count || 0}
              loading={loadingCampaigns[row.id]}
              currentPage={campaignsPage[row.id] || 1}
              onPageChange={(page) => fetchCampaigns(row.id, page)}
            />
          )}
          expandableRowExpanded={row => expandedRows.includes(row.id)}
          onRowExpandToggled={(toggled, row) => handleRowToggle(row)}
          expandableRowsHideExpander
        />
        <Tooltip id="expand-tooltip" className="z-[1005]" />
        <Tooltip id="view-tooltip" className="z-[1005]" />
      </div>
    </div>
  );
};

export default Table;